import * as yup from "yup";
import { getStateOptions } from "./locationData";

const isFilled = (value) => String(value ?? "").trim() !== "";

/** True when the country has a visible State/Province dropdown. */
export const countryHasStates = (country) =>
  (getStateOptions(country) || []).length > 0;

/**
 * Returns per-field error messages for a location object.
 * @param {{ address?: string, city?: string, state?: string, country?: string }} location
 * @param {{ mode?: 'always' | 'allOrNothing' }} [options]
 *   - always: every visible field required (use when a location row is shown/added)
 *   - allOrNothing: if any field has a value, all visible fields are required; all-empty is OK
 */
export function getLocationFieldErrors(
  location,
  { mode = "always" } = {},
) {
  const address = location?.address ?? location?.address1 ?? "";
  const city = location?.city ?? "";
  const state = location?.state ?? "";
  const country = location?.country ?? "";

  const hasAny =
    isFilled(address) || isFilled(city) || isFilled(state) || isFilled(country);

  if (mode === "allOrNothing" && !hasAny) {
    return {};
  }

  const errors = {};
  if (!isFilled(address)) errors.address = "Street address is required";
  if (!isFilled(city)) errors.city = "City is required";
  if (!isFilled(country)) errors.country = "Country is required";
  if (countryHasStates(country) && !isFilled(state)) {
    errors.state = "State/Province is required";
  }
  return errors;
}

/**
 * Apply LocationFields-compatible errors for one prefix (e.g. "primary", "additional_0_").
 * @returns {boolean} true when valid
 */
export function applyLocationFieldErrors(
  { setError, clearErrors },
  prefix,
  location,
  options,
) {
  const keys = [
    `${prefix}Address`,
    `${prefix}City`,
    `${prefix}State`,
    `${prefix}Country`,
  ];
  keys.forEach((key) => clearErrors?.(key));

  const fieldErrors = getLocationFieldErrors(location, options);
  const suffixByField = {
    address: "Address",
    city: "City",
    state: "State",
    country: "Country",
  };

  Object.entries(fieldErrors).forEach(([field, message]) => {
    setError(`${prefix}${suffixByField[field]}`, {
      type: "manual",
      message,
    });
  });

  return Object.keys(fieldErrors).length === 0;
}

/**
 * Validate every additional location row (row present ⇒ visible fields required).
 * @returns {boolean} true when all rows are valid
 */
export function validateAdditionalLocations(
  locations,
  { setError, clearErrors },
  { maxClear = 30 } = {},
) {
  const list = Array.isArray(locations) ? locations : [];
  for (let i = 0; i < Math.max(list.length, maxClear); i++) {
    clearErrors?.(`additional_${i}_Address`);
    clearErrors?.(`additional_${i}_City`);
    clearErrors?.(`additional_${i}_State`);
    clearErrors?.(`additional_${i}_Country`);
  }

  let ok = true;
  list.forEach((loc, index) => {
    const valid = applyLocationFieldErrors(
      { setError, clearErrors: undefined },
      `additional_${index}_`,
      loc,
      { mode: "always" },
    );
    if (!valid) ok = false;
  });
  return ok;
}

/**
 * Yup shape for LocationFields (`${prefix}Address|City|State|Country`).
 *
 * Uses field-level `.test()` (not cross-field `.when`) to avoid yup cyclic deps.
 *
 * @param {string} prefix e.g. "primary" | "provider"
 * @param {{
 *   mode?: 'always' | 'allOrNothing',
 *   whenField?: string,
 *   whenIs?: any | ((value: any) => boolean),
 * }} [options]
 */
export function locationFieldsSchema(prefix, options = {}) {
  const { mode = "always", whenField, whenIs } = options;

  const addressKey = `${prefix}Address`;
  const cityKey = `${prefix}City`;
  const stateKey = `${prefix}State`;
  const countryKey = `${prefix}Country`;

  const isWhenActive = (parent) => {
    if (!whenField) return true;
    const value = parent?.[whenField];
    if (typeof whenIs === "function") return Boolean(whenIs(value));
    return value === whenIs;
  };

  const isGroupActive = (parent) => {
    if (!isWhenActive(parent)) return false;
    if (mode === "allOrNothing") {
      return (
        isFilled(parent?.[addressKey]) ||
        isFilled(parent?.[cityKey]) ||
        isFilled(parent?.[stateKey]) ||
        isFilled(parent?.[countryKey])
      );
    }
    return true;
  };

  const requiredWhenActive = (message, { isState = false } = {}) =>
    yup.string().test({
      name: "location-field-required",
      message,
      test(value) {
        const parent = this.parent;
        if (!isGroupActive(parent)) return true;
        if (isState && !countryHasStates(parent?.[countryKey])) return true;
        return isFilled(value);
      },
    });

  return {
    [addressKey]: requiredWhenActive("Street address is required"),
    [cityKey]: requiredWhenActive("City is required"),
    [stateKey]: requiredWhenActive("State/Province is required", {
      isState: true,
    }),
    [countryKey]: requiredWhenActive("Country is required"),
  };
}
